import { useCallback } from 'react';
import { InstantSearch } from 'react-instantsearch-hooks';

import { useTypesense } from '../../hooks/useTypesense';
import { InputAutocomplete, InputProps } from './InputAutocomplete';

export type AutocompleteInputProps = InputProps & {
  hitValueKey?: string;
  label?: string;
  displayValue?: 'name' | 'id';
  includeAllOption?: boolean;
  includeNullOption?: boolean;
  isPortaled?: boolean;
};

export const AirlineInput = ({
  hitValueKey = 'airline_id',
  isMulti = false,
  displayValue = 'name',
  includeAllOption = false,
  includeNullOption = false,
  ...props
}: AutocompleteInputProps) => {
  const { searchClient } = useTypesense({
    additionalSearchParameters: {
      query_by: 'airline_id, airline_name',
    },
  });

  const getDisplayValue = useCallback(
    (hit: any) => {
      if (displayValue === 'id') return hit.airline_id;
      return `${hit.airline_name} (${hit.airline_id})`;
    },
    [displayValue],
  );

  const hitMap = useCallback(
    (hits: any[]) => {
      const options = hits.map((hit) => ({
        label: getDisplayValue(hit),
        value: hit?.[hitValueKey],
      }));

      if (includeAllOption) {
        return [{ label: 'All Available', value: 'ALL' }, ...options];
      }

      if (includeNullOption) {
        return [{ label: '-', value: null }, ...options];
      }

      return options;
    },
    [includeAllOption, includeNullOption, getDisplayValue, hitValueKey],
  );

  if (!searchClient) return null;

  return (
    <InstantSearch searchClient={searchClient} indexName="airlines">
      <InputAutocomplete hitMap={hitMap} isMulti={isMulti} {...props} />
    </InstantSearch>
  );
};
